import { memo, useState } from 'react';
import { Check, ChevronDown, Pencil } from 'lucide-react';
import type { Recommendation } from '@shared/types';
import { Badge, Button, Td, Tr } from '@/components/ui';
import { cn } from '@/lib/cn';
import { severity as sevMap, recommendationStatus as statusMap } from '@/lib/status';
import { formatCompactCop, formatNumber, formatShortDate } from '@shared/format';
import { useApproveRecommendation, useAdjustRecommendation } from '@/lib/hooks';

/** El punto de color reemplaza a la columna Urgencia cuando ésta no entra (móvil). */
const PUNTO: Record<Recommendation['severity'], string> = {
  critico: 'bg-danger',
  alto: 'bg-warning',
  medio: 'bg-info',
};

const BORDE: Record<Recommendation['severity'], string> = {
  critico: 'border-l-danger',
  alto: 'border-l-warning',
  medio: 'border-l-info',
};

/**
 * Una fila de la tabla de recomendaciones, con su detalle plegado.
 *
 * Va memoizada, y el handler que recibe llega con `useCallback` desde arriba: sin las dos
 * cosas el `memo` no sirve y volvemos al problema medido en este proyecto (una tabla larga
 * re-renderizando entera con cada tecla).
 */
export const FilaRecomendacion = memo(function FilaRecomendacion({
  rec,
  elegida,
  onElegir,
}: {
  rec: Recommendation;
  elegida: boolean;
  onElegir: (id: string) => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const [editando, setEditando] = useState(false);
  const [qty, setQty] = useState(rec.qtyRecommended);
  const aprobar = useApproveRecommendation();
  const ajustar = useAdjustRecommendation();
  const sev = sevMap[rec.severity];
  const st = statusMap[rec.status];
  const decidible = rec.status !== 'aprobado';

  return (
    <>
      <Tr className={cn('border-l-2', BORDE[rec.severity], elegida && 'bg-primary-soft/50')}>
        <Td className="w-9 pr-0">
          <input
            type="checkbox"
            checked={elegida}
            disabled={!decidible}
            onChange={() => onElegir(rec.id)}
            aria-label={`Elegir ${rec.productName}`}
            className="h-4 w-4 accent-primary disabled:opacity-30"
          />
        </Td>
        <Td>
          <button
            onClick={() => setAbierta((a) => !a)}
            className="block max-w-[26rem] text-left hover:underline"
          >
            <span className="flex items-center gap-1.5">
              <span
                className={cn('h-1.5 w-1.5 shrink-0 rounded-full md:hidden', PUNTO[rec.severity])}
                aria-hidden
              />
              <span className="truncate text-sm font-medium text-ink-strong">{rec.productName}</span>
            </span>
            <span className="block truncate font-mono text-2xs text-ink-faint">
              {rec.sku} · {rec.supplier}
            </span>
            {/* El costo tiene columna propia desde `sm`. Abajo de eso no entra ninguna
                columna más, y era justo el otro número de la decisión: se repite acá. */}
            <span className="block truncate text-2xs text-ink-muted sm:hidden">
              {rec.costEstimate > 0 ? `${formatCompactCop(rec.costEstimate)} COP` : 'sin costo unitario'}
            </span>
          </button>
        </Td>
        <Td className="hidden md:table-cell">
          <Badge tone={sev.tone}>{sev.label}</Badge>
        </Td>
        <Td align="right" className="hidden lg:table-cell">
          <span className="whitespace-nowrap text-ink-strong">{formatShortDate(rec.orderBefore)}</span>
          {rec.overdue && <span className="ml-1.5 text-2xs text-danger-text">vencido</span>}
        </Td>
        <Td align="right" className="hidden xl:table-cell">
          <span className="text-ink">{formatNumber(rec.currentStock)}</span>
        </Td>
        <Td align="right" className="hidden lg:table-cell">
          <span className={cn(rec.coverageDays <= 0 ? 'font-semibold text-danger-text' : 'text-ink')}>
            {rec.coverageDays}d
          </span>
        </Td>
        <Td align="right">
          <span className="cifra text-base">{formatNumber(rec.qtyRecommended)}</span>
          {rec.qtyIa != null && rec.qtyIa !== rec.qtyRecommended && (
            <span className="block text-2xs text-info-text">IA: {formatNumber(rec.qtyIa)}</span>
          )}
        </Td>
        <Td align="right" className="hidden sm:table-cell">
          {rec.costEstimate > 0 ? (
            <span className="font-medium text-ink-strong">{formatCompactCop(rec.costEstimate)}</span>
          ) : (
            // Un 0 acá se leería como "sale gratis" en vez de "falta el dato".
            <span className="text-2xs text-warning-text">sin costo</span>
          )}
        </Td>
        <Td className="hidden xl:table-cell">
          {/* "Pendiente" es el estado del 100% de las filas mientras no decidas nada: ponerlo
              en todas es ruido que no distingue a ninguna. Se pinta sólo lo ya decidido. */}
          {rec.status === 'pendiente' ? (
            <span className="text-2xs text-ink-ghost">—</span>
          ) : (
            <Badge tone={st.tone}>{st.label}</Badge>
          )}
        </Td>
        <Td align="right" className="w-10 pl-0">
          <button
            onClick={() => setAbierta((a) => !a)}
            aria-label={abierta ? 'Cerrar el detalle' : 'Ver el detalle'}
            aria-expanded={abierta}
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-faint hover:bg-hover hover:text-ink-strong"
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', abierta && 'rotate-180')} />
          </button>
        </Td>
      </Tr>

      {abierta && (
        <tr className="border-b border-hairline/60 bg-subtle">
          <td colSpan={10} className="px-4 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 max-w-prosa">
                <p className="section-label">Por qué esta cantidad</p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{rec.rationale}</p>
                {rec.decisionNote && (
                  <p className="mt-2 rounded-lg border border-hairline bg-card p-2.5 text-xs text-ink-muted">
                    {rec.decisionNote}
                  </p>
                )}
                {rec.decidedBy && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-success-text">
                    <Check className="h-3.5 w-3.5" />
                    {st.label} por {rec.decidedBy}
                  </p>
                )}
              </div>

              {decidible && (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {editando ? (
                    <>
                      <label className="flex items-center gap-2 text-xs text-ink-muted">
                        Comprar
                        <input
                          type="number"
                          value={qty}
                          min={1}
                          onChange={(e) => setQty(Number(e.target.value))}
                          className="h-9 w-28 rounded-lg border border-primary bg-card px-2 text-right text-sm text-ink-strong focus:outline-none focus:ring-2 focus:ring-primary-ring/40"
                        />
                        uds
                      </label>
                      <Button
                        variant="primary"
                        disabled={ajustar.isPending || qty <= 0}
                        onClick={() =>
                          ajustar.mutate({ id: rec.id, qty }, { onSuccess: () => setEditando(false) })
                        }
                      >
                        {ajustar.isPending ? 'Guardando…' : 'Guardar'}
                      </Button>
                      <Button variant="ghost" onClick={() => { setEditando(false); setQty(rec.qtyRecommended); }}>
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="success"
                        icon={<Check className="h-4 w-4" />}
                        disabled={aprobar.isPending}
                        onClick={() => aprobar.mutate(rec.id)}
                      >
                        {aprobar.isPending ? 'Aprobando…' : 'Aprobar y crear la orden'}
                      </Button>
                      <Button variant="outline" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => setEditando(true)}>
                        Ajustar la cantidad
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});
